'use client';

import { addDays, formatDate, formatToISODate, localDate } from '@/lib/utils';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  DeliveryTimeDate,
  DeliveryTimeSlot,
  DeliveryDataSchema,
  DeliveryDisabledDate,
} from '@/models/DeliveryData';
import { tr } from 'date-fns/locale/tr';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from './ui/button';
import { ChevronRight } from '@/icons/ChevronRight';
import { useToast } from '@/components/ui/use-toast';
import { Checkbox } from './ui/checkbox';
import { Alert, AlertDescription } from './ui/alert';
import { AlertCircle, X } from 'lucide-react';
import { Spinner } from './ui/spinner';
import { ScrollArea } from './ui/scroll-area';
import { ScreenSize, useScreenSize } from '@/lib/hook/useScreenSize';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { AllowedProductsData } from '@/models/DeliveryData';
import AllowedProductDialog from './AllowedProductDialog';
import dynamic from 'next/dynamic';
import SmartImage from './SmartImage';

const Calendar = dynamic(() => import('@/components/ui/calendar').then((mod) => mod.Calendar), {
  ssr: false,
  loading: () => <div className="p-4">Takvim Yükleniyor...</div>,
});

const RawHtml = ({ html, style }) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.innerHTML = html;
    }
  }, [html]);
  return <div ref={ref} style={style} />;
};

export default function ProductOrderDateOptions({
  dateChooserRef,
  schemas,
  deliveryDates,
  disabledDates,
  selectedDeliveryDate,
  onDeliveryDateChange,
  selectedDeliveryTime,
  onDeliveryTimeChange,
  selectedDirectButton,
  onDirectButtonChange,
  datePickerSelected,
  onDatePickerSelected,
  customDate,
  onCustomDateChange,
  deliveryDatesLoading,
  deliveryTimesLoadFailed,
  isLocationSelected,
  hasDeliveryOptionsHandler,
  decorationReservedByDate,
  useAppointmentLabels,
}: {
  dateChooserRef?: React.Ref<HTMLDivElement>;
  schemas?: DeliveryDataSchema[];
  deliveryDates?: DeliveryTimeDate[];
  disabledDates?: DeliveryDisabledDate[];
  selectedDeliveryDate?: DeliveryTimeDate;
  onDeliveryDateChange: (data: DeliveryTimeDate) => void;
  selectedDeliveryTime?: string;
  onDeliveryTimeChange: (data: string) => void;
  selectedDirectButton?: DeliveryTimeSlot;
  onDirectButtonChange: (data?: DeliveryTimeSlot) => void;
  datePickerSelected: boolean;
  onDatePickerSelected: (data: boolean) => void;
  customDate?: Date;
  onCustomDateChange: (data?: Date) => void;
  deliveryDatesLoading: boolean;
  deliveryTimesLoadFailed?: boolean;
  isLocationSelected: boolean;
  hasDeliveryOptionsHandler: (status: boolean) => void;
  /** Araç süslemesi: tarih bazında dolu saat dilimi id'leri (order servisi reservedTimes) */
  decorationReservedByDate?: Record<string, number[]>;
  /** Araç süsleme: takvim/saat metinleri randevu dilinde */
  useAppointmentLabels?: boolean;
}) {
  const [visibleDirectButtons, setVisibleDirectButtons] = useState<DeliveryTimeSlot[]>([]);
  const [visibleDeliveryTimes, setVisibleDeliveryTimes] = useState<DeliveryTimeSlot[]>([]);
  const [isTimePickerVisible, setIsTimePickerVisible] = useState(false);
  const screenSize = useScreenSize();
  const [deliveryWarning, setDeliveryWarning] = useState<string | undefined>(undefined);
  const [showDeliveryWarning, setShowDeliveryWarning] = useState(false);
  const [closeReason, setCloseReason] = useState<string | undefined>(undefined);
  const [hoveredCloseReason, setHoveredCloseReason] = useState<{
    dateKey: string;
    reason: string;
  } | null>(null);
  const [allowedProductsData, setAllowedProductsData] = useState<AllowedProductsData | undefined>(
    undefined,
  );
  const [allowedProductDialogVisible, setAllowedProductDialogVisible] = useState(false);

  const { toast } = useToast();
  const appt = Boolean(useAppointmentLabels);
  const reservedSlotHint = appt ? 'Rezervasyonlu' : 'Dolu';
  const calendarSubLabel = appt ? 'Randevu tarihi seçin' : 'Tarih seçin';
  const calendarPickPlaceholder = appt ? 'Randevu tarihi seçiniz' : 'Tarih seçiniz';
  const hourPlaceholder = appt ? 'Randevu saati seçiniz' : 'Saat seçiniz';
  const hourSheetTitle = appt ? 'Randevu Saati Seçin' : 'Saat Seçin';
  const hourSelectFallback = appt ? 'Randevu Saati Seçiniz' : 'Saat Seçiniz';

  const closedDateMap = useMemo(() => {
    const map = new Map<string, string>();
    (disabledDates || []).forEach((item) => {
      if (item?.date) {
        map.set(String(item.date).slice(0, 10), item.reason || '');
      }
    });
    (deliveryDates || []).forEach((d) => {
      if (d?.disabled && d?.date) {
        map.set(String(d.date).slice(0, 10), d.disabledReason || '');
      }
    });
    return map;
  }, [disabledDates, deliveryDates]);

  useEffect(() => {
    const genericSchema = schemas?.find(
      (item) => !item.rule.weekday && !item.rule.range?.start && !item.rule.range?.end,
    );

    const selectedDate =
      (selectedDeliveryDate?.date ? new Date(selectedDeliveryDate.date) : undefined) ||
      customDate ||
      new Date();

    // Compare with a stable time so date-only ranges work predictably.
    const anchor = new Date(selectedDate);
    anchor.setHours(12, 0, 0, 0);
    const weekDay = (anchor.getDay() + 7) % 8;

    const schemaApplies = (item: DeliveryDataSchema) => {
      const rangeStart = item.rule.range?.start;
      const rangeEnd = item.rule.range?.end;
      const hasRange = Boolean(rangeStart && rangeEnd);
      const inRange = hasRange
        ? anchor >= new Date(rangeStart!) && anchor <= new Date(rangeEnd!)
        : false;
      const inWeekday = Boolean(item.rule.weekday?.includes(weekDay));
      return inRange || inWeekday;
    };

    // Quick delivery applies to today only — never show it if today is disabled.
    const today = deliveryDates?.find((d) => d?.today);
    if (today?.disabled) {
      setVisibleDirectButtons([]);
      return;
    }

    const eligibleSchema = schemas?.find(schemaApplies);
    setVisibleDirectButtons(
      eligibleSchema ? eligibleSchema.directButtons : genericSchema?.directButtons || [],
    );

    const warningSchema = schemas?.find(
      (item) => item.deliveryTimes.length === 0 && Boolean(item.warning),
    );
    if (warningSchema?.allowedProductsData) {
      setAllowedProductsData(warningSchema.allowedProductsData);
    } else {
      setAllowedProductsData(undefined);
    }

    // Only show warning when the warning schema actually applies to the selected day.
    const warningForSelectedDay =
      warningSchema && schemaApplies(warningSchema) ? warningSchema.warning : undefined;
    setDeliveryWarning(warningForSelectedDay);
  }, [schemas, selectedDeliveryDate?.date, customDate, deliveryDates]);

  useEffect(() => {
    const todayClosed = deliveryDates?.find((d) => d?.today && d?.disabled && d?.disabledReason);
    setCloseReason(todayClosed?.disabledReason);
  }, [deliveryDates]);

  // Selected date için aktif teslimat saati yoksa (desktop/mobil fark etmeksizin) dropdown yerine uyarı göster.
  const hasPickedDate = Boolean(selectedDeliveryDate?.date) || Boolean(customDate);
  const shouldShowNoDeliveryTimesAlert =
    hasPickedDate &&
    Boolean(schemas?.length) &&
    !selectedDirectButton &&
    !deliveryDatesLoading &&
    visibleDeliveryTimes.length === 0;

  const dateKeyForReserved = (d?: DeliveryTimeDate, cal?: Date): string | undefined => {
    if (d?.date) return d.date.slice(0, 10);
    if (cal) return formatToISODate(cal);
    return undefined;
  };

  const isDecorationSlotReserved = (dateKey: string | undefined, slotId: string | number) => {
    if (!dateKey || !decorationReservedByDate) return false;
    const blocked = decorationReservedByDate[dateKey] || [];
    return blocked.includes(Number(slotId));
  };

  const withoutReservedSlots = (slots: DeliveryTimeSlot[], dateKey: string | undefined) => {
    if (!dateKey || !decorationReservedByDate) return slots;
    const blocked = decorationReservedByDate[dateKey] || [];
    return slots.filter((dt) => !blocked.includes(Number(dt.id)));
  };

  useEffect(() => {
    if (selectedDeliveryDate?.date) {
      setVisibleDeliveryTimes(getActiveDeliveryTimes(new Date(selectedDeliveryDate.date)));
      return;
    }

    if (customDate) {
      setVisibleDeliveryTimes(getActiveDeliveryTimes(customDate));
      return;
    }
  }, [selectedDeliveryDate, customDate, decorationReservedByDate, schemas]);

  useEffect(() => {
    if ((!schemas || schemas.length === 0) && isLocationSelected && !deliveryDatesLoading) {
      hasDeliveryOptionsHandler?.(false);
    } else if (schemas?.length || 0 > 0) {
      hasDeliveryOptionsHandler?.(true);
    }
  }, [schemas, isLocationSelected, deliveryDatesLoading]);

  const setDate = (deliveryDate: DeliveryTimeDate) => {
    const dateKey = deliveryDate.date.slice(0, 10);
    const allForDay = getActiveDeliveryTimes(new Date(deliveryDate.date)).filter((deliveryTime) =>
      checkDeliveryTimeIsActive(deliveryTime, deliveryDate),
    );
    const activeDeliveryTimes = withoutReservedSlots(allForDay, dateKey);
    const disabledBySlots =
      (deliveryDate.today && activeDeliveryTimes.length === 0) || activeDeliveryTimes.length === 0;
    const disabled = Boolean(deliveryDate.disabled) || disabledBySlots;

    if (disabled) {
      if (deliveryDate.today && deliveryDate.disabledReason) {
        setCloseReason(deliveryDate.disabledReason);
      }
      if (deliveryDate.date === allowedProductsData?.checkDate) {
        setAllowedProductDialogVisible(true);
        setShowDeliveryWarning(true);
      }
      return;
    }
    onDeliveryDateChange(deliveryDate);

    if (screenSize === ScreenSize.SM) {
      setIsTimePickerVisible(true);
    }
  };

  const checkDeliveryTimeIsActive = (
    deliveryTime: DeliveryTimeSlot,
    deliveryDate: DeliveryTimeDate | undefined,
  ): boolean => {
    const activeDeliveryDate = deliveryDate || selectedDeliveryDate;
    return (
      !activeDeliveryDate?.today ||
      new Date() < new Date(`${activeDeliveryDate?.date} ${deliveryTime.deadline}`)
    );
  };

  const getActiveDeliveryTimes = (date?: Date): DeliveryTimeSlot[] => {
    const genericSchema = schemas?.find(
      (item) => !item.rule.weekday && !item.rule.range?.start && !item.rule.range?.end,
    );

    if (!date) {
      return genericSchema?.deliveryTimes || [];
    }

    const time = new Date().toLocaleTimeString('tr');

    date.setHours(parseInt(time.substring(0, 2), 10));
    date.setMinutes(parseInt(time.substring(3, 5), 10));
    date.setSeconds(parseInt(time.substring(7, 9), 10));

    const weekDayOfToday = (date.getDay() + 7) % 8;

    const eligibleShema = schemas?.find((item) => {
      const isTodayInRange =
        item.rule.range?.start &&
        item.rule.range?.end &&
        date >= new Date(item.rule.range.start) &&
        date <= new Date(item.rule.range.end);
      return item.rule.weekday?.includes(weekDayOfToday) || isTodayInRange;
    });

    const delTimes = eligibleShema
      ? eligibleShema.deliveryTimes
      : genericSchema?.deliveryTimes || [];

    return delTimes.filter((deliveryTime) =>
      checkDeliveryTimeIsActive(deliveryTime, {
        date: date.toLocaleString('lt').substring(0, 10),
        today: true,
        dayName: '',
        id: '',
        month: '',
        day: 0,
      }),
    );
  };

  const isCalendarDateClosed = (date?: Date) => {
    if (!date) return false;
    return closedDateMap.has(formatToISODate(date));
  };

  const handleCalendarSelect = (date?: Date) => {
    if (!date) {
      onCustomDateChange(date);
      return;
    }
    const calKey = formatToISODate(date);
    const closeMsg = closedDateMap.get(calKey);
    if (closeMsg) {
      toast({
        variant: 'destructive',
        description: closeMsg,
      });
      return;
    }
    const calSlots = getActiveDeliveryTimes(date);
    if (
      localDate(date) === allowedProductsData?.checkDate &&
      withoutReservedSlots(calSlots, calKey).length === 0
    ) {
      setAllowedProductDialogVisible(true);
      setShowDeliveryWarning(true);
      return;
    }
    onCustomDateChange(date);
  };

  return (
    <div className="flex flex-col gap-2" ref={dateChooserRef}>
      {deliveryWarning && (showDeliveryWarning || !allowedProductsData) && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{deliveryWarning}</AlertDescription>
        </Alert>
      )}
      {closeReason && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{closeReason}</AlertDescription>
        </Alert>
      )}
      {shouldShowNoDeliveryTimesAlert && !deliveryWarning && !closeReason && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Bu tarihte ürün teslimatı yapılamıyor.</AlertDescription>
        </Alert>
      )}
      {(!schemas || schemas.length === 0) && isLocationSelected ? (
        <>
          {deliveryDatesLoading ? (
            <div className="w-full flex flex-col justify-center items-center">
              <Spinner className="size-8 text-hzc_purple_500 mx-auto" />
              <span className="text-xl font-medium text-hzc_beige_900 mx-auto text-center">
                Yükleniyor...
              </span>
            </div>
          ) : (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {deliveryTimesLoadFailed ? (
                  <>
                    Şu anda ürün bilgilerini alamadım.{' '}
                    <button
                      type="button"
                      className="underline cursor-pointer"
                      onClick={() => window.location.reload()}
                    >
                      Sayfayı yenile
                    </button>
                  </>
                ) : (
                  'Stoklarımız bittiği için şimdilik bu ürünün teslimatını sağlayamıyoruz.'
                )}
              </AlertDescription>
            </Alert>
          )}
        </>
      ) : (
        <>
          {visibleDirectButtons.map((direct) => {
            return (
              <div key={direct.id} className="relative mt-2">
                <SmartImage
                  width={24}
                  height={24}
                  src="/ic_direct_delivery.png"
                  className="absolute -top-2.5 -left-2.5 bg-hzc_beige_200 rounded-full"
                  alt="Hızlı teslimat"
                  style={{ height: 'auto' }}
                  imageType="icon"
                />
                <div
                  className="flex items-center gap-2 content-center border-2 border-dashed border-[#B25AE2] bg-[#F7EFF9] w-fit rounded-[10px] px-3 py-1 hover:cursor-pointer"
                  onClick={() => {
                    if (checkDeliveryTimeIsActive(direct, undefined)) {
                      onDirectButtonChange(selectedDirectButton ? undefined : direct);
                    } else {
                      toast({
                        variant: 'destructive',
                        description: 'Hızlı teslimat aktif değil',
                      });
                    }
                  }}
                >
                  <Checkbox
                    checked={selectedDirectButton?.id === direct.id}
                    onCheckedChange={() => {}}
                    aria-label={direct.title || ''}
                    className="size-[16px] flex-shrink-0 data-[state=checked]:bg-hzc_purple_500 data-[state=checked]:border-0 bg-white border-2 border-[#E5D6E9]"
                  />
                  <span className="text-[14px] bg-gradient-to-b from-[#4457DE] to-[#BE5CE9] bg-clip-text text-transparent font-semibold">
                    {direct.title}
                  </span>
                </div>
              </div>
            );
          })}
          <div className="grid grid-cols-4 gap-2">
            {/* Active Dates */}
            {!selectedDirectButton &&
              deliveryDates?.map((deliveryDate, index) => {
                const dateKey = deliveryDate.date.slice(0, 10);
                const allDaySlots = getActiveDeliveryTimes(new Date(deliveryDate.date)).filter(
                  (deliveryTime) => checkDeliveryTimeIsActive(deliveryTime, deliveryDate),
                );
                const activeDeliveryTimes = withoutReservedSlots(allDaySlots, dateKey);
                const disabledBySlots =
                  (deliveryDate.today && activeDeliveryTimes.length === 0) ||
                  activeDeliveryTimes.length === 0;
                const disabled = Boolean(deliveryDate.disabled) || disabledBySlots;

                return (
                  <div
                    className="flex flex-col gap-2 relative"
                    key={`deliveryDate_${index}`}
                    onMouseEnter={() => {
                      if (disabled && !deliveryDate.today && deliveryDate.disabledReason) {
                        setHoveredCloseReason({ dateKey, reason: deliveryDate.disabledReason });
                      } else {
                        setHoveredCloseReason(null);
                      }
                    }}
                    onMouseLeave={() => setHoveredCloseReason(null)}
                  >
                    <div
                      className={`${
                        disabled
                          ? 'diagonal-dashed hover:cursor-not-allowed bg-[#b1b0a8]'
                          : 'hover:cursor-pointer bg-[#ECEAE1]'
                      } flex flex-col text-center justify-center transition-all h-[80px] w-full px-2 rounded-[16px] items-center
                    ${deliveryDate.date === selectedDeliveryDate?.date ? 'bg-white' : ``} 
                ${
                  deliveryDate.date === selectedDeliveryDate?.date
                    ? 'border-2 border-[#EDEDF0]'
                    : 'border border-hzc_beige_400'
                }
                `}
                      onClick={() => setDate(deliveryDate)}
                      style={{
                        ...(deliveryDate?.dayStyle ? deliveryDate?.dayStyle : {}),
                        ...(deliveryDate.date === selectedDeliveryDate?.date &&
                        deliveryDate?.dayStyle?.selected
                          ? deliveryDate?.dayStyle?.selected
                          : {}),
                        ...(disabled && deliveryDate?.dayStyle?.disabled
                          ? deliveryDate?.dayStyle?.disabled
                          : {}),
                      }}
                    >
                      {deliveryDate?.dayStyle?.childDiv && (
                        <RawHtml
                          style={{
                            ...(deliveryDate?.dayStyle?.childStyle
                              ? deliveryDate?.dayStyle?.childStyle
                              : {}),
                          }}
                          html={deliveryDate?.dayStyle?.childDiv}
                        />
                      )}
                      <span
                        className={`transition-all font-bold text-[14px] sm:text-[16px] md:text-[18px] lg:text-[20px] font-fraunces text-[#2B3280]`}
                        style={{
                          ...(deliveryDate?.dayStyle?.color
                            ? { color: deliveryDate?.dayStyle?.color }
                            : {}),
                          ...(deliveryDate.date === selectedDeliveryDate?.date &&
                          deliveryDate?.dayStyle?.selected?.color
                            ? { color: deliveryDate?.dayStyle?.selected?.color }
                            : {}),
                          ...(disabled
                            ? {
                                color: deliveryDate?.dayStyle?.disabled?.color || '#898989',
                              }
                            : {}),
                        }}
                      >
                        {deliveryDate.dayName.includes('\n') ? (
                          deliveryDate.dayName.split('\n').map((line, idx) => (
                            <span key={idx} className="block">
                              {line}
                            </span>
                          ))
                        ) : (
                          <>{deliveryDate.dayName}</>
                        )}
                      </span>

                      {(deliveryDate.day || deliveryDate.month) && (
                        <span
                          className={`flex transition-all font-medium text-[12px] text-[#5C5C57]}`}
                          style={{
                            ...(disabled
                              ? {
                                  color: deliveryDate?.dayStyle?.disabled?.color || '#898989',
                                }
                              : {}),
                          }}
                        >
                          {`${deliveryDate.day ? deliveryDate.day : ''}${
                            deliveryDate.month ? ' ' + deliveryDate.month : ''
                          }`}
                        </span>
                      )}
                    </div>
                    <div className="hidden sm:flex">
                      {deliveryDate.date === selectedDeliveryDate?.date &&
                        (allDaySlots.length > 0 ? (
                          <Select
                            value={selectedDeliveryTime}
                            onValueChange={(value) => {
                              const deliveryTime = visibleDeliveryTimes?.find(
                                (it) => it.id === value,
                              );

                              if (
                                deliveryTime &&
                                checkDeliveryTimeIsActive(deliveryTime, undefined) &&
                                !isDecorationSlotReserved(dateKey, value)
                              ) {
                                onDeliveryTimeChange(value);
                              } else {
                                toast({
                                  variant: 'destructive',
                                  description: 'Seçtiğiniz tarih geçersiz.',
                                });
                              }
                            }}
                          >
                            <SelectTrigger
                              value={selectedDeliveryTime}
                              className="rounded-[10px] border-2 border-[#EDEDF0] bg-white text-[#212121] text-[12px] font-medium focus:ring-transparent"
                              style={{
                                ...(deliveryDate.date === selectedDeliveryDate?.date &&
                                deliveryDate?.dayStyle?.selected
                                  ? deliveryDate?.dayStyle?.selected
                                  : {}),
                              }}
                            >
                              <SelectValue placeholder={hourPlaceholder} />
                            </SelectTrigger>
                            <SelectContent
                              className="bg-white rounded-lg text-[#5C5C57] text-[12px] font-medium focus:ring-transparent border-2 border-[#EDEDF0]"
                              style={{
                                ...(deliveryDate.date === selectedDeliveryDate?.date &&
                                deliveryDate?.dayStyle?.selected
                                  ? deliveryDate?.dayStyle?.selected
                                  : {}),
                              }}
                            >
                              <SelectGroup>
                                {allDaySlots.map((deliveryTime) => {
                                  const reserved = isDecorationSlotReserved(
                                    dateKey,
                                    deliveryTime.id,
                                  );
                                  return (
                                    <SelectItem
                                      className={`text-[12px] ${
                                        reserved
                                          ? 'opacity-50 cursor-not-allowed'
                                          : `hover:cursor-pointer ${
                                              deliveryDate?.dayStyle?.hoverBackgroundColor
                                                ? ''
                                                : 'focus:bg-[#ECEAE1]'
                                            }`
                                      }`}
                                      value={deliveryTime.id}
                                      key={deliveryTime.id}
                                      disabled={reserved}
                                    >
                                      {reserved
                                        ? `${deliveryTime.title} · ${reservedSlotHint}`
                                        : deliveryTime.title}
                                    </SelectItem>
                                  );
                                })}
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        ) : null)}
                    </div>
                    {hoveredCloseReason?.dateKey === dateKey && (
                      <div className="absolute left-1/2 -translate-x-1/2 -top-2 -translate-y-full z-50 pointer-events-none">
                        <div className="max-w-[220px] text-center text-[12px] leading-snug bg-black text-white px-3 py-2 rounded-md shadow-lg">
                          {hoveredCloseReason.reason}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            {/* Choose Date From Calendar */}
            {!selectedDirectButton && deliveryDates && (
              <div className="flex flex-col gap-2">
                <div
                  className={`flex flex-col text-center justify-center transition-all h-[80px] w-full px-2 rounded-[16px] hover:cursor-pointer items-center ${
                    datePickerSelected
                      ? 'bg-white border-2 border-[#EDEDF0]'
                      : 'bg-[#ECEAE1] border-none'
                  } `}
                  onClick={() => onDatePickerSelected(!datePickerSelected)}
                >
                  <span
                    className={`flex transition-all font-bold text-[14px] sm:text-[16px] md:text-[18px] lg:text-[20px] font-fraunces text-[#2B3280]`}
                  >
                    Takvim
                  </span>
                  <span className={`flex transition-all font-medium text-[12px] text-[#5C5C57]}`}>
                    {calendarSubLabel}
                  </span>
                </div>
                {datePickerSelected && (
                  <div className="hidden sm:flex flex-col gap-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button className="rounded-[10px] border-2 border-[#EDEDF0] bg-white hover:bg-white text-[#212121] text-[12px] font-medium focus:ring-transparent px-3">
                          <div className="flex justify-between w-full text-center">
                            {(customDate && formatDate(customDate)) || calendarPickPlaceholder}
                            <ChevronRight className="text-hzc_purple_300 text-[10px] rotate-90 my-auto" />
                          </div>
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          locale={tr}
                          mode="single"
                          selected={customDate}
                          onSelect={handleCalendarSelect}
                          disabled={isCalendarDateClosed}
                          fromDate={addDays(
                            new Date(`${deliveryDates?.find((_) => true)?.date}`),
                            1,
                          )}
                          toDate={addDays(
                            new Date(`${deliveryDates?.find((_) => true)?.date}`),
                            61,
                          )}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>

                    {visibleDeliveryTimes && visibleDeliveryTimes.length > 0 && (
                      <Select
                        value={selectedDeliveryTime}
                        onValueChange={(value) => {
                          const dk = dateKeyForReserved(selectedDeliveryDate, customDate);
                          if (isDecorationSlotReserved(dk, value)) {
                            toast({
                              variant: 'destructive',
                              description: 'Bu saat dilimi rezerve.',
                            });
                            return;
                          }
                          onDeliveryTimeChange(value);
                        }}
                      >
                        <SelectTrigger className="rounded-[10px] border-2 border-[#EDEDF0] bg-white text-[#212121] text-[12px] font-medium focus:ring-transparent">
                          <SelectValue placeholder={hourPlaceholder} />
                        </SelectTrigger>
                        <SelectContent className="bg-white rounded-lg text-[#5C5C57] text-[12px] font-medium focus:ring-transparent border-2 border-[#EDEDF0]">
                          <SelectGroup>
                            {visibleDeliveryTimes?.map((deliveryTime) => {
                              if (!checkDeliveryTimeIsActive(deliveryTime, undefined)) {
                                return <div key={deliveryTime.id} />;
                              }
                              const dk = dateKeyForReserved(selectedDeliveryDate, customDate);
                              const reserved = isDecorationSlotReserved(dk, deliveryTime.id);
                              return (
                                <SelectItem
                                  className={`text-[12px] focus:bg-[#ECEAE1] ${
                                    reserved
                                      ? 'opacity-50 cursor-not-allowed'
                                      : 'hover:cursor-pointer'
                                  }`}
                                  value={deliveryTime.id}
                                  key={deliveryTime.id}
                                  disabled={reserved}
                                >
                                  {reserved
                                    ? `${deliveryTime.title} · ${reservedSlotHint}`
                                    : deliveryTime.title}
                                </SelectItem>
                              );
                            })}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2 sm:hidden">
            {/* Choose Date From Calendar */}
            {!selectedDirectButton && datePickerSelected && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button className="rounded-[10px] border-2 border-[#EDEDF0] bg-white hover:bg-white text-[#212121] text-[12px] font-medium focus:ring-transparent px-3">
                    <div className="flex justify-between w-full text-center">
                      {(customDate && formatDate(customDate)) || calendarPickPlaceholder}
                      <ChevronRight className="text-hzc_purple_300 text-[10px] rotate-90 my-auto" />
                    </div>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    locale={tr}
                    mode="single"
                    selected={customDate}
                    onSelect={handleCalendarSelect}
                    disabled={isCalendarDateClosed}
                    fromDate={addDays(new Date(`${deliveryDates?.find((_) => true)?.date}`), 1)}
                    toDate={addDays(new Date(`${deliveryDates?.find((_) => true)?.date}`), 61)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            )}
            {/* Choose Time */}
            {(selectedDeliveryDate?.date || customDate) && !selectedDirectButton && (
              <>
                {visibleDeliveryTimes.length > 0 ? (
                  <Sheet open={isTimePickerVisible} onOpenChange={setIsTimePickerVisible}>
                    <SheetTrigger asChild>
                      <div className="w-full">
                        <Select>
                          <SelectTrigger
                            className="rounded-[10px] border-2 border-[#EDEDF0] bg-white text-[#212121] text-[12px] font-medium focus:ring-transparent"
                            style={{
                              ...(selectedDeliveryDate?.date === selectedDeliveryDate?.date &&
                              selectedDeliveryDate?.dayStyle?.selected
                                ? selectedDeliveryDate?.dayStyle?.selected
                                : {}),
                            }}
                          >
                            <SelectValue
                              placeholder={`${
                                visibleDeliveryTimes.find((it) => it.id === selectedDeliveryTime)
                                  ?.title || hourSelectFallback
                              }`}
                            />
                          </SelectTrigger>
                        </Select>
                      </div>
                    </SheetTrigger>
                    <SheetContent
                      className="bg-[#2B3280] border-none p-0"
                      side="bottom"
                      closeEnabled={false}
                    >
                      <div className="flex flex-col p-2">
                        <div className="flex flex-row items-center justify-center border-b-2 border-[#4D5394] pb-2 relative h-12">
                          <span className="text-[16px] w-fit text-white font-medium">
                            {hourSheetTitle}
                          </span>
                          <Button
                            className="text-white hover:bg-transparent absolute right-0 bg-transparent"
                            onClick={() => setIsTimePickerVisible(false)}
                          >
                            <X className="text-white size-6" />
                          </Button>
                        </div>
                        <ScrollArea className="h-72 pt-2">
                          {visibleDeliveryTimes?.map((deliveryTime) => {
                            const deliveryDate = selectedDeliveryDate;
                            const isActive =
                              !deliveryDate?.today ||
                              new Date() <
                                new Date(`${deliveryDate?.date} ${deliveryTime.deadline}`);

                            if (!isActive) {
                              return <div key={deliveryTime.id} />;
                            }

                            const dk = dateKeyForReserved(selectedDeliveryDate, customDate);
                            const reserved = isDecorationSlotReserved(dk, deliveryTime.id);
                            if (reserved) {
                              return (
                                <div
                                  className="text-[14px] text-[#9CA3C7] h-[48px] border-b-2 border-[#4D5394] text-start flex items-center cursor-not-allowed opacity-80"
                                  key={deliveryTime.id}
                                >
                                  <span>
                                    {`${deliveryTime.start} - ${deliveryTime.end}`}
                                    <span className="ml-2 text-[12px]">({reservedSlotHint})</span>
                                  </span>
                                </div>
                              );
                            }

                            return (
                              <div
                                className="text-[14px] text-white h-[48px] border-b-2 border-[#4D5394] text-start flex items-center focus:bg-transparent hover:cursor-pointer"
                                key={deliveryTime.id}
                                onClick={() => {
                                  onDeliveryTimeChange(deliveryTime.id);
                                  setIsTimePickerVisible(false);
                                }}
                              >
                                {`${deliveryTime.start} - ${deliveryTime.end}`}
                              </div>
                            );
                          })}
                        </ScrollArea>
                      </div>
                    </SheetContent>
                  </Sheet>
                ) : null}
              </>
            )}
          </div>
        </>
      )}
      {allowedProductsData?.visible && (
        <AllowedProductDialog
          visible={allowedProductDialogVisible}
          onClose={() => setAllowedProductDialogVisible(false)}
          allowedProductsData={allowedProductsData}
        />
      )}
    </div>
  );
}
